import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PublicNav, PublicFooter } from "@/components/public/PublicChrome";

/* =============================================================================
 * LANDING — Harf
 *
 * ART DIRECTION: "the baseline".
 * Arabic script hangs off a single horizontal line, and classical calligraphy
 * is proportioned in rhombic dots. The page borrows both: a hairline rule runs
 * through every section, content hangs off it, and nothing is centred except
 * the closing CTA. Palette comes from illuminated manuscripts — carbon ink,
 * lapis, gold leaf, and the red that was historically used for the vowel
 * marks (used here only for the harakat ticks and micro-accents).
 *
 * Arabic glyphs are set in Amiri (a naskh revival) — never in the UI sans.
 *
 * STILL TO DO
 *  - HERO IMAGE: /public/images/oasis-hero.png (transparent PNG). It now sits
 *    ON the baseline rule with a large ح watermark behind it. If the PNG is
 *    missing the layout still holds (the glyph carries the hero).
 *  - VIDEOS: the three YouTube IDs are unchanged; each plate has a caption
 *    line under it, edit those captions when the videos are recut.
 *  - COPY CHECK: the "vowel marks disappear" row claims reading practice moves
 *    from vowelled to bare text as level goes up. Confirm that matches what
 *    Reading Practice actually does, or soften the line.
 * ========================================================================== */

function YoutubeFacade({ videoId }) {
    const [loaded, setLoaded] = useState(false);
    const [thumbError, setThumbError] = useState(false);

    if (loaded) {
        return (
            <iframe
                width="100%"
                height="100%"
                src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1`}
                title="Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ position: "absolute", inset: 0 }}
            />
        );
    }

    return (
        <button
            className="plate-btn"
            onClick={() => setLoaded(true)}
            aria-label="Play video"
        >
            {!thumbError && (
                <img
                    src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
                    alt=""
                    onError={() => setThumbError(true)}
                    className="plate-thumb"
                />
            )}
            <span className="plate-play">
                <span className="plate-play-tri" />
            </span>
        </button>
    );
}

/* Problems first — this is the spine of the page. */
const FRICTIONS = [
    {
        glyph: "بـ ـبـ ـب",
        problem: "The letters change shape",
        answer: "Most apps only ever show the isolated form. Harf drills the joined forms from lesson one.",
    },
    {
        glyph: "َ ِ ُ",
        problem: "Then the vowels vanish",
        answer: "Reading practice moves from fully vowelled text to bare text as your level climbs.",
    },
    {
        glyph: "ذاكرة",
        problem: "Forty words in, six out",
        answer: "Everything you meet goes on a spaced-repetition schedule and returns before you lose it.",
    },
    {
        glyph: "أ ← ب",
        problem: "No one says what comes first",
        answer: "Content runs A1 to C2, so the next thing to learn is the next thing on the page.",
    },
    {
        glyph: "٣",
        problem: "Week three is where it ends",
        answer: "Your oasis grows out of the reviews you actually do. The habit gets something to hold.",
    },
];

const FEATURES = [
    {
        kicker: "Letters",
        title: "Read joined script",
        points: [
            "28 letters, the hamza, the harakat",
            "Initial, medial, final forms and the ligatures",
            "Handwriting order for each letter",
            "A focus list for the ones you keep missing",
        ],
        youtubeId: "Zq7WSGerpiM",
        caption: "Letter lessons and the focus list",
    },
    {
        kicker: "Vocabulary and grammar",
        title: "Reviews timed to the day you would forget",
        points: [
            "4,000 words and 200 patterns on one schedule",
            "Sorted A1 to C2",
            "Example sentence and audio on every entry",
            "Progress tracked per word",
        ],
        youtubeId: "w4kre9L3YIo",
        caption: "A review session, start to finish",
        reverse: true,
    },
    {
        kicker: "Consistency",
        title: "An oasis that grows when you study",
        points: [
            "Decorations unlocked by reviews, not by paying",
            "A streak worth keeping, made visible",
            "Milestones across all four skills",
        ],
        youtubeId: "qrKb_YRx8KE",
        caption: "Building and customising the oasis",
    },
];

const ALSO_INCLUDED = [
    "Conjugation drill",
    "Root practice",
    "Sentence builder",
    "Story builder",
    "Numbers practice",
    "Reading practice",
];

/* Ordered by what a cold visitor needs to believe, in order. */
const TESTIMONIALS = [
    {
        i: "LF",
        name: "Léa Fontaine",
        loc: "France",
        title: "Zero to B1 material in two months",
        body: "I had never written a letter of Arabic. Two months later I was working through B1 vocabulary without help.",
    },
    {
        i: "AC",
        name: "Amelia Clarke",
        loc: "United Kingdom",
        title: "It replaced Duolingo in a week",
        body: "No filler, no guilt-trip notifications. The reviews land right at the edge of forgetting.",
    },
    {
        i: "DK",
        name: "Daniel Kim",
        loc: "United States",
        title: "Nothing else was complete",
        body: "Four other apps, each missing a piece. This one has the audio, the grammar and the order.",
    },
    {
        i: "JP",
        name: "Julien Petit",
        loc: "France",
        title: "Two years of circles, fixed",
        body: "I never knew what to study next. The roadmap sorted that in a week and I have not had a gap since.",
    },
    {
        i: "OI",
        name: "Omar Idrissi",
        loc: "Morocco",
        title: "Not built to farm my attention",
        body: "Cheapest serious Arabic tool I found, and the only one that does not treat me like a slot machine.",
    },
    {
        i: "NH",
        name: "Nora Haddad",
        loc: "Tunisia",
        title: "The only one I kept past week two",
        body: "Still the first thing I open on the train. Arabic stopped feeling like homework.",
    },
];

const FAQ = [
    {
        q: "Why should I choose Harf?",
        a: "Harf combines everything you need to master Arabic in one place. We feature a Spaced Repetition System (SRS) for efficient vocabulary and grammar retention, complete content organized on the CEFR scale from A1 to C2, interactive oasis building that gamifies your learning journey, comprehensive progress tracking for letters, vocabulary, grammar, and conjugation, fun practice games, and a learning roadmap that guides you from beginner to advanced. Built by Arabic learners, for Arabic learners. We've tried many tools and created Harf to include every feature we wished existed.",
    },
    {
        q: "I am a complete beginner, can I use Harf?",
        a: "Absolutely. When you sign up you go through an onboarding that assesses your current level, sets your goals, and starts you on the alphabet if you are new. The roadmap then shows your path from the basics to advanced. You learn at your own pace with content from CEFR A1 all the way to C2.",
    },
    {
        q: "Can I use Harf on my phone?",
        a: 'Yes. Harf works on mobile as a PWA (Progressive Web App). On iOS, use Safari\'s "Add to Home Screen". On Android, install directly from Chrome. Desktop users can install from Chrome, Edge, or other modern browsers. Once installed it works like a native app, with offline capabilities. Native iOS and Android apps are in development.',
    },
    {
        q: "What's included in the premium trial?",
        a: "The 7-day trial gives you complete access to every Premium feature: unlimited vocabulary, grammar, conjugation and letter learning, unlimited SRS reviews and practice sessions, full CEFR content from A1 to C2, oasis building and customization, all premium minigames and practice modes, and advanced progress tracking. No credit card required to start.",
    },
    {
        q: "What happens after the premium trial?",
        a: "You either subscribe to Premium or Lifetime to keep unlimited access, or you continue on a free account with daily limits: 5 vocabulary words per day, 2 grammar points per day, 100 SRS reviews per day. Letter and conjugation practice stay unlimited. Free accounts have restricted access to the premium practice modes (Root Practice, Numbers Practice, Reading Practice, Sentence Builder, Story Builder), view-only access to the oasis, and limited progress statistics. You can subscribe at any time. No card is required during the trial and you are never charged unless you choose to subscribe.",
    },
    {
        q: "Is the Lifetime plan really lifetime?",
        a: "Yes. Lifetime is a one-time payment covering all current and future Premium features and updates, an exclusive lifetime member badge, exclusive oasis items, and no recurring charges. Lifetime access is valid for the duration of Harf's service operation. While we are committed to providing long-term service, lifetime holders acknowledge access is valid for as long as Harf continues to operate. All plans are subject to our Terms of Service.",
    },
];

export default function Landing() {
    const { user } = useAuth();
    const nav = useNavigate();
    const [openFaq, setOpenFaq] = useState(null);

    useEffect(() => {
        if (user && user.onboarded) nav("/dashboard");
    }, [user, nav]);

    useEffect(() => {
        const title = "Harf — Learn to read and write Arabic";
        const description =
            "Arabic from the joined alphabet to real text: spaced-repetition vocabulary, grammar and conjugation, organised A1 to C2. Free for 7 days, no card.";
        document.title = title;
        const setMeta = (attr, key, content) => {
            let tag = document.head.querySelector(`meta[${attr}="${key}"]`);
            if (!tag) {
                tag = document.createElement("meta");
                tag.setAttribute(attr, key);
                document.head.appendChild(tag);
            }
            tag.setAttribute("content", content);
        };
        setMeta("name", "description", description);
        setMeta("property", "og:title", title);
        setMeta("property", "og:description", description);
    }, []);

    return (
        <div className="hf-land">
            <style>{`
@import url('https://fonts.googleapis.com/css2?family=Amiri:wght@400;700&family=Newsreader:opsz,wght@6..72,400;6..72,500;6..72,600&family=Inter:wght@400;500;600&display=swap');

.hf-land {
  --paper: #ECEDF1;
  --paper-deep: #E2E4EA;
  --ink: #14172A;
  --ink-soft: #5D6179;
  --lapis: #1F3FA8;
  --gold: #B0862F;
  --harakat: #B8412E;
  --rule: #C9CCD6;
  --brand: var(--lapis);

  background: var(--paper);
  color: var(--ink);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 16px;
  line-height: 1.6;
  overflow-x: hidden;
  -webkit-font-smoothing: antialiased;
}
.hf-land * { box-sizing: border-box; }
.hf-land ul { list-style: none; padding: 0; margin: 0; }
.hf-land p { margin: 0; }
.hf-land h1, .hf-land h2, .hf-land h3 {
  font-family: 'Newsreader', Georgia, serif;
  font-weight: 500;
  letter-spacing: -0.015em;
  margin: 0;
}
.hf-land .ar { font-family: 'Amiri', 'Noto Naskh Arabic', serif; direction: rtl; }
.hf-land a { color: inherit; text-decoration: none; }
.hf-land :focus-visible { outline: 2px solid var(--lapis); outline-offset: 3px; }

.hf-land .wrap { max-width: 1120px; margin: 0 auto; padding: 0 28px; position: relative; }

/* The baseline: every band hangs off a hairline rule */
.hf-land .band { border-top: 1px solid var(--rule); padding: 84px 0; }
.hf-land .band-head { display: grid; grid-template-columns: minmax(0, 7fr) minmax(0, 5fr); gap: 48px; align-items: end; margin-bottom: 56px; }
.hf-land .band-head h2 { font-size: clamp(28px, 3.4vw, 40px); line-height: 1.12; max-width: 13ch; }
.hf-land .band-head p { color: var(--ink-soft); font-size: 15px; max-width: 46ch; }
.hf-land .tick { display: inline-block; width: 26px; height: 1px; background: var(--harakat); vertical-align: 6px; margin-right: 12px; }

/* ---------- HERO ---------- */
.hf-land .hero { padding: 108px 0 0; }
.hf-land .hero-grid { display: grid; grid-template-columns: minmax(0, 6fr) minmax(0, 5fr); gap: 56px; align-items: end; }
.hf-land .hero h1 { font-size: clamp(40px, 6.2vw, 72px); line-height: 1.02; letter-spacing: -0.03em; max-width: 12ch; }
.hf-land .hero .lede { margin-top: 26px; font-size: 17.5px; color: var(--ink-soft); max-width: 50ch; }
.hf-land .hero-actions { margin-top: 34px; display: flex; align-items: center; gap: 22px; flex-wrap: wrap; }
.hf-land .btn-ink { background: var(--ink); color: var(--paper); padding: 15px 30px; border-radius: 2px; font-size: 15px; font-weight: 600; display: inline-block; transition: background .18s ease; }
.hf-land .btn-ink:hover { background: var(--lapis); }
.hf-land .hero-note { font-size: 13.5px; color: var(--ink-soft); }
.hf-land .hero-note a { border-bottom: 1px solid var(--rule); font-weight: 500; color: var(--ink); }
.hf-land .hero-art { position: relative; min-height: 300px; display: flex; align-items: flex-end; justify-content: center; }
.hf-land .hero-glyph { position: absolute; bottom: -8px; left: 50%; transform: translateX(-50%); font-size: clamp(200px, 26vw, 330px); line-height: .78; color: var(--lapis); opacity: .1; user-select: none; pointer-events: none; }
.hf-land .hero-art img { position: relative; width: min(400px, 100%); height: auto; z-index: 2; animation: drift 9s ease-in-out infinite; }
.hf-land .hero-rule { margin-top: 56px; height: 1px; background: linear-gradient(90deg, var(--ink) 0%, var(--ink) 22%, var(--rule) 22%, var(--rule) 100%); }
.hf-land .hero-strip { display: flex; flex-wrap: wrap; gap: 10px 34px; padding: 18px 0 0; font-size: 13px; color: var(--ink-soft); }
.hf-land .hero-strip b { color: var(--ink); font-weight: 600; }
@keyframes drift { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-14px); } }

/* ---------- FRICTIONS ---------- */
.hf-land .friction { display: grid; grid-template-columns: 92px minmax(0, 5fr) minmax(0, 6fr); gap: 40px; align-items: baseline; padding: 30px 0; border-top: 1px solid var(--rule); }
.hf-land .friction:first-of-type { border-top: none; }
.hf-land .friction-glyph { font-size: 27px; color: var(--lapis); line-height: 1.4; white-space: nowrap; }
.hf-land .friction h3 { font-size: 22px; line-height: 1.25; }
.hf-land .friction p { color: var(--ink-soft); font-size: 15px; max-width: 62ch; }

/* ---------- FEATURE PLATES ---------- */
.hf-land .plate-row { display: grid; grid-template-columns: minmax(0, 5fr) minmax(0, 7fr); gap: 60px; align-items: center; padding: 54px 0; border-top: 1px solid var(--rule); }
.hf-land .plate-row:first-of-type { border-top: none; padding-top: 0; }
.hf-land .plate-row.reverse .p-text { order: 2; }
.hf-land .plate-row.reverse .p-media { order: 1; }
.hf-land .p-kicker { font-size: 13px; color: var(--harakat); font-weight: 600; margin-bottom: 12px; }
.hf-land .p-text h3 { font-size: clamp(24px, 2.6vw, 31px); line-height: 1.15; margin-bottom: 22px; max-width: 16ch; }
.hf-land .p-text li { display: grid; grid-template-columns: 18px 1fr; gap: 10px; font-size: 15px; color: var(--ink-soft); padding: 9px 0; border-top: 1px solid var(--rule); }
.hf-land .p-text li:first-child { border-top: none; }
.hf-land .p-text li::before { content: "•"; color: var(--gold); }
.hf-land .p-media figure { margin: 0; }
.hf-land .plate { position: relative; aspect-ratio: 16/9; background: var(--ink); border: 1px solid var(--ink); border-radius: 2px; overflow: hidden; box-shadow: 10px 10px 0 var(--paper-deep); }
.hf-land .plate video { width: 100%; height: 100%; object-fit: cover; }
.hf-land .plate-btn { position: absolute; inset: 0; border: none; padding: 0; background: none; cursor: pointer; display: flex; align-items: center; justify-content: center; }
.hf-land .plate-thumb { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; opacity: .85; transition: opacity .2s ease; }
.hf-land .plate-btn:hover .plate-thumb { opacity: 1; }
.hf-land .plate-play { position: relative; z-index: 2; width: 62px; height: 62px; border-radius: 50%; background: var(--paper); display: flex; align-items: center; justify-content: center; transition: transform .18s ease; }
.hf-land .plate-btn:hover .plate-play { transform: scale(1.06); }
.hf-land .plate-play-tri { width: 0; height: 0; border-style: solid; border-width: 8px 0 8px 14px; border-color: transparent transparent transparent var(--ink); margin-left: 4px; }
.hf-land .plate-caption { margin-top: 14px; font-size: 13px; color: var(--ink-soft); padding-left: 2px; }

.hf-land .chips { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 46px; }
.hf-land .chip { border: 1px solid var(--rule); border-radius: 2px; padding: 7px 13px; font-size: 13.5px; color: var(--ink); background: transparent; }
.hf-land .chips-label { font-size: 13.5px; color: var(--ink-soft); align-self: center; margin-right: 6px; }
.hf-land .inline-link { display: inline-block; margin-top: 28px; font-size: 14.5px; font-weight: 600; color: var(--lapis); border-bottom: 1px solid var(--lapis); padding-bottom: 2px; }

/* ---------- TESTIMONIALS ---------- */
.hf-land .t-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0 44px; }
.hf-land .t-card { border-top: 1px solid var(--rule); padding: 26px 0 30px; }
.hf-land .t-title { font-family: 'Newsreader', Georgia, serif; font-size: 19px; line-height: 1.25; margin-bottom: 10px; }
.hf-land .t-body { font-size: 14.5px; color: var(--ink-soft); }
.hf-land .t-foot { display: flex; align-items: center; gap: 10px; margin-top: 18px; }
.hf-land .t-mark { width: 28px; height: 28px; border-radius: 50%; border: 1px solid var(--lapis); color: var(--lapis); font-size: 11px; font-weight: 600; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.hf-land .t-name { font-size: 13.5px; font-weight: 600; }
.hf-land .t-loc { font-size: 12.5px; color: var(--ink-soft); }

/* ---------- CTA ---------- */
.hf-land .cta { background: var(--ink); color: var(--paper); padding: 96px 0; text-align: center; }
.hf-land .cta h2 { font-size: clamp(30px, 4.4vw, 48px); line-height: 1.08; max-width: 18ch; margin: 0 auto; }
.hf-land .cta .ar-mark { font-size: 46px; color: var(--gold); opacity: .75; margin-bottom: 22px; }
.hf-land .cta p { color: #A7ACC4; font-size: 15.5px; margin: 20px auto 0; max-width: 44ch; }
.hf-land .btn-paper { display: inline-block; margin-top: 32px; background: var(--paper); color: var(--ink); padding: 15px 32px; border-radius: 2px; font-size: 15px; font-weight: 600; transition: background .18s ease; }
.hf-land .btn-paper:hover { background: var(--gold); color: var(--ink); }

/* ---------- FAQ ---------- */
.hf-land .faq-list { max-width: 760px; }
.hf-land .faq-item { border-top: 1px solid var(--rule); }
.hf-land .faq-item:last-child { border-bottom: 1px solid var(--rule); }
.hf-land .faq-q { width: 100%; display: flex; align-items: baseline; justify-content: space-between; gap: 24px; padding: 20px 0; background: none; border: none; color: var(--ink); text-align: left; cursor: pointer; font-family: 'Newsreader', Georgia, serif; font-size: 18px; }
.hf-land .faq-sign { color: var(--harakat); font-size: 17px; transition: transform .22s ease; flex-shrink: 0; }
.hf-land .faq-item.open .faq-sign { transform: rotate(45deg); }
.hf-land .faq-a { display: grid; grid-template-rows: 0fr; transition: grid-template-rows .26s ease; }
.hf-land .faq-item.open .faq-a { grid-template-rows: 1fr; }
.hf-land .faq-a > div { overflow: hidden; }
.hf-land .faq-a p { font-size: 14.5px; color: var(--ink-soft); max-width: 66ch; padding-bottom: 22px; }

@media (max-width: 900px) {
  .hf-land .hero { padding-top: 76px; }
  .hf-land .hero-grid, .hf-land .band-head, .hf-land .plate-row { grid-template-columns: 1fr; gap: 32px; }
  .hf-land .band-head { margin-bottom: 40px; }
  .hf-land .plate-row.reverse .p-text, .hf-land .plate-row.reverse .p-media { order: 0; }
  .hf-land .plate-row:first-of-type { padding-top: 0; }
  .hf-land .friction { grid-template-columns: 1fr; gap: 12px; }
  .hf-land .t-grid { grid-template-columns: 1fr 1fr; gap: 0 32px; }
  .hf-land .plate { box-shadow: 6px 6px 0 var(--paper-deep); }
}
@media (max-width: 620px) {
  .hf-land .t-grid { grid-template-columns: 1fr; }
  .hf-land .band { padding: 60px 0; }
}
@media (prefers-reduced-motion: reduce) {
  .hf-land * { animation: none !important; transition: none !important; }
}
      `}</style>

            <PublicNav />

            {/* HERO */}
            <header className="hero">
                <div className="wrap">
                    <div className="hero-grid">
                        <div>
                            <h1>Learn to actually read Arabic</h1>
                            <p className="lede">
                                The joined alphabet, 4,000 words on a
                                spaced-repetition schedule, grammar in a sane
                                order. Modern Standard, A1 to C2.
                            </p>
                            <div className="hero-actions">
                                <Link
                                    to="/auth?mode=signup"
                                    className="btn-ink"
                                >
                                    Start learning
                                </Link>
                                <span className="hero-note">
                                    7 days free, no card.{" "}
                                    <Link to="/auth">Log in</Link>
                                </span>
                            </div>
                        </div>
                        <div className="hero-art">
                            <span className="hero-glyph ar" aria-hidden="true">
                                حرف
                            </span>
                            <img
                                src="/images/oasis-hero.png"
                                alt=""
                                onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                }}
                            />
                        </div>
                    </div>
                    <div className="hero-rule" />
                    <div className="hero-strip">
                        <span>
                            <b>4,000</b> words and sentences
                        </span>
                        <span>
                            <b>28</b> letters in every joined form
                        </span>
                        <span>
                            <b>200</b> grammar patterns
                        </span>
                        <span>
                            <b>A1 → C2</b> on the CEFR scale
                        </span>
                    </div>
                </div>
            </header>

            {/* FRICTIONS — the spine */}
            <section className="band" id="why">
                <div className="wrap">
                    <div className="band-head">
                        <h2>
                            <span className="tick" />
                            Five reasons people give up on Arabic
                        </h2>
                        <p>
                            None of them are about talent. Each is a gap in how
                            the language is taught.
                        </p>
                    </div>
                    {FRICTIONS.map((f) => (
                        <div key={f.problem} className="friction">
                            <div
                                className="friction-glyph ar"
                                aria-hidden="true"
                            >
                                {f.glyph}
                            </div>
                            <h3>{f.problem}</h3>
                            <p>{f.answer}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* FEATURES */}
            <section className="band" id="features">
                <div className="wrap">
                    <div className="band-head">
                        <h2>
                            <span className="tick" />
                            How Harf closes them
                        </h2>
                    </div>

                    {FEATURES.map((f) => (
                        <div
                            key={f.title}
                            className={`plate-row${f.reverse ? " reverse" : ""}`}
                        >
                            <div className="p-text">
                                <div className="p-kicker">{f.kicker}</div>
                                <h3>{f.title}</h3>
                                <ul>
                                    {f.points.map((p) => (
                                        <li key={p}>
                                            <span>{p}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="p-media">
                                <figure>
                                    <div className="plate">
                                        <YoutubeFacade videoId={f.youtubeId} />
                                    </div>
                                    <figcaption className="plate-caption">
                                        {f.caption}
                                    </figcaption>
                                </figure>
                            </div>
                        </div>
                    ))}

                    <div className="chips">
                        <span className="chips-label">Also included</span>
                        {ALSO_INCLUDED.map((t) => (
                            <span key={t} className="chip">
                                {t}
                            </span>
                        ))}
                    </div>
                    <Link to="/features" className="inline-link">
                        See every feature
                    </Link>
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section className="band">
                <div className="wrap">
                    <div className="band-head">
                        <h2>
                            <span className="tick" />
                            They had already tried the others
                        </h2>
                    </div>
                    <div className="t-grid">
                        {TESTIMONIALS.map((t, idx) => (
                            <div key={idx} className="t-card">
                                <div className="t-title">{t.title}</div>
                                <div className="t-body">{t.body}</div>
                                <div className="t-foot">
                                    <div className="t-mark">{t.i}</div>
                                    <div>
                                        <div className="t-name">{t.name}</div>
                                        <div className="t-loc">{t.loc}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="cta">
                <div className="wrap">
                    <div className="ar-mark ar" aria-hidden="true">
                        ابدأ
                    </div>
                    <h2>Your first letter takes about four minutes</h2>
                    <p>Seven days of full access, no card.</p>
                    <Link to="/auth?mode=signup" className="btn-paper">
                        Start learning
                    </Link>
                </div>
            </section>

            {/* FAQ */}
            <section className="band" id="faq">
                <div className="wrap">
                    <div className="band-head">
                        <h2>
                            <span className="tick" />
                            Questions we get asked
                        </h2>
                    </div>
                    <div className="faq-list">
                        {FAQ.map((f, idx) => {
                            const open = openFaq === idx;
                            return (
                                <div
                                    key={f.q}
                                    className={`faq-item${open ? " open" : ""}`}
                                >
                                    <button
                                        className="faq-q"
                                        aria-expanded={open}
                                        onClick={() =>
                                            setOpenFaq(open ? null : idx)
                                        }
                                    >
                                        <span>{f.q}</span>
                                        <span className="faq-sign">+</span>
                                    </button>
                                    <div className="faq-a">
                                        <div>
                                            <p>{f.a}</p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            <PublicFooter />
        </div>
    );
}
